import {
  Briefcase,
  Calendar,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileText,
  FileSignature,
  Handshake,
  KeyRound,
  Landmark,
  Logs as LogsIcon,
  LogOut,
  LucideIcon,
  Menu,
  Moon,
  Package,
  Receipt,
  Search,
  Settings,
  Sun,
  UserRound,
  Users,
  WalletCards,
  X,
} from 'lucide-react'
import { lazy, ReactNode, Suspense, useEffect, useRef, useState } from 'react'
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'

import * as Sentry from '@sentry/react'
import { CommissionCard } from '../features/commercial_commissions/CommissionCard'
import { AgentConfiguration } from '../features/commercial_commissions/AgentConfiguration'
import { CommissionsPage } from '../features/commercial_commissions/CommissionsPage'
import { isFinanceCoreUiEnabled } from '../features/finance/featureFlag'
import { useCommissionAccess } from '../shared/api/generated/commercial-commissions/commercial-commissions'

import { useListProfessionalsEndpointApiProfessionalsGet } from '../shared/api/generated/professionals/professionals'
import { STORAGE_KEYS } from '../shared/lib/constants'
import { CoreFinanceSlotContext } from '../shared/ui/CoreFinanceSlot'
import { ToastProvider } from '../shared/ui/Toast'
import { AuthProvider, useAuth } from './auth'
import {
  BreadcrumbProvider,
  useBreadcrumbOverride,
  useCurrentBreadcrumbs,
  type Crumb,
} from './breadcrumbs'
import { DoctorsProvider, useDoctors } from './doctors'
import { SettingsHome, SettingsLayout } from './settings/SettingsLayout'
import { isSettingsPath, SETTINGS_HOME } from './settings/settingsNav'

import '../App.css'

// Páginas carregadas sob demanda (code-splitting). Antes tudo entrava num
// único bundle (~1,65 MB) baixado no primeiro acesso; agora cada rota vira um
// chunk separado, carregado só quando navegada. Exports nomeados → adaptados
// pro contrato default do lazy().
const AgendaPage = lazy(() =>
  import('../features/agenda/AgendaPage').then((m) => ({ default: m.AgendaPage })),
)
const BankAccountsPage = lazy(() =>
  import('../features/config/BankAccountsPage').then((m) => ({ default: m.BankAccountsPage })),
)
const ConfigPage = lazy(() =>
  import('../features/config/ConfigPage').then((m) => ({ default: m.ConfigPage })),
)
const ConfigLegacyRedirect = lazy(() =>
  import('../features/config/ConfigPage').then((m) => ({ default: m.ConfigLegacyRedirect })),
)
const TeamPage = lazy(() =>
  import('../features/config/TeamPage').then((m) => ({ default: m.TeamPage })),
)
const ClinicServicesPage = lazy(() =>
  import('../features/config/ClinicServicesPage').then((m) => ({
    default: m.ClinicServicesPage,
  })),
)
const PaymentMethodsPage = lazy(() =>
  import('../features/config/PaymentMethodsPage').then((m) => ({ default: m.PaymentMethodsPage })),
)
const PriceTablesPage = lazy(() =>
  import('../features/config/PriceTablesPage').then((m) => ({ default: m.PriceTablesPage })),
)
const RequirementsPage = lazy(() =>
  import('../features/config/RequirementsPage').then((m) => ({ default: m.RequirementsPage })),
)
const ProcedureCatalogPage = lazy(() =>
  import('../features/config/ProcedureCatalogPage').then((m) => ({
    default: m.ProcedureCatalogPage,
  })),
)
const RepasseRulesPage = lazy(() =>
  import('../features/config/RepasseRulesPage').then((m) => ({ default: m.RepasseRulesPage })),
)
const SupplierCostTablesPage = lazy(() =>
  import('../features/config/SupplierCostTablesPage').then((m) => ({
    default: m.SupplierCostTablesPage,
  })),
)
const DoctorPage = lazy(() =>
  import('../features/doctors/DoctorPage').then((m) => ({ default: m.DoctorPage })),
)
const NotionImportBanner = lazy(() =>
  import('../features/professionals/NotionImportBanner').then((m) => ({
    default: m.NotionImportBanner,
  })),
)
const ProfessionalWorkPage = lazy(() =>
  import('../features/professionals/ProfessionalDetailPage').then((m) => ({
    default: m.ProfessionalWorkPage,
  })),
)
const ProfessionalsPage = lazy(() =>
  import('../features/professionals/ProfessionalsPage').then((m) => ({
    default: m.ProfessionalsPage,
  })),
)
const PublicDoctorRepassesPage = lazy(() =>
  import('../features/doctors/PublicDoctorRepassesPage').then((m) => ({
    default: m.PublicDoctorRepassesPage,
  })),
)
const FinancialV2Page = lazy(() =>
  import('../features/financial_v2/FinancialV2Page').then((m) => ({ default: m.FinancialV2Page })),
)
const ReceivablesPage = lazy(() =>
  import('../features/receivables/ReceivablesPage').then((m) => ({ default: m.ReceivablesPage })),
)
const LoginPage = lazy(() =>
  import('../features/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const ForgotPasswordPage = lazy(() =>
  import('../features/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
)
const SetPasswordPage = lazy(() =>
  import('../features/auth/SetPasswordPage').then((m) => ({ default: m.SetPasswordPage })),
)
const ChangePasswordPage = lazy(() =>
  import('../features/auth/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })),
)
const LogsPage = lazy(() =>
  import('../features/logs/LogsPage').then((m) => ({ default: m.LogsPage })),
)
const PatientDossierPage = lazy(() =>
  import('../features/patients/PatientDossierPage').then((m) => ({
    default: m.PatientDossierPage,
  })),
)
const PatientFeegowBridgePage = lazy(() =>
  import('../features/patients/PatientFeegowBridgePage').then((m) => ({
    default: m.PatientFeegowBridgePage,
  })),
)
const PatientsPage = lazy(() =>
  import('../features/patients/PatientsPage').then((m) => ({ default: m.PatientsPage })),
)
const ProposalEditorPage = lazy(() =>
  import('../features/proposals/ProposalEditorPage').then((m) => ({
    default: m.ProposalEditorPage,
  })),
)
const ProposalPage = lazy(() =>
  import('../features/proposals/ProposalPage').then((m) => ({ default: m.ProposalPage })),
)
const ProposalPrintPage = lazy(() =>
  import('../features/proposals/ProposalPrintPage').then((m) => ({ default: m.ProposalPrintPage })),
)
const ProposalPresentPage = lazy(() =>
  import('../features/proposals/ProposalPresentPage').then((m) => ({
    default: m.ProposalPresentPage,
  })),
)
const ProposalsPage = lazy(() =>
  import('../features/proposals/ProposalsPage').then((m) => ({ default: m.ProposalsPage })),
)
const SupplierPricesPage = lazy(() =>
  import('../features/suppliers/SupplierPricesPage').then((m) => ({
    default: m.SupplierPricesPage,
  })),
)
const SuppliersPage = lazy(() =>
  import('../features/suppliers/SuppliersPage').then((m) => ({ default: m.SuppliersPage })),
)
const SurgicalCasesPage = lazy(() =>
  import('../features/surgical_cases/SurgicalCasesPage').then((m) => ({
    default: m.SurgicalCasesPage,
  })),
)
const SurgicalCaseDetailPage = lazy(() =>
  import('../features/surgical_cases/SurgicalCaseDetailPage').then((m) => ({
    default: m.SurgicalCaseDetailPage,
  })),
)
const SurgeriesPage = lazy(() =>
  import('../features/surgeries/SurgeriesPage').then((m) => ({ default: m.SurgeriesPage })),
)
const SurgeryDetailPage = lazy(() =>
  import('../features/surgeries/SurgeryDetailPage').then((m) => ({ default: m.SurgeryDetailPage })),
)
const NotionPublishModal = lazy(() =>
  import('../features/proposals/NotionPublishModal').then((m) => ({ default: m.NotionPublishModal })),
)
const SignaturesPage = lazy(() =>
  import('../features/signatures/SignaturesPage').then((m) => ({ default: m.SignaturesPage })),
)
const SurgeryFinancePage = lazy(() =>
  import('../features/finance/SurgeryFinancePage').then((m) => ({ default: m.SurgeryFinancePage })),
)
const SurgeryFinanceEmbedded = lazy(() =>
  import('../features/finance/SurgeryFinancePage').then((m) => ({ default: m.SurgeryFinanceEmbedded })),
)
const ProviderEventsPage = lazy(() =>
  import('../features/finance/ProviderEventsPage').then((m) => ({ default: m.ProviderEventsPage })),
)
const ClinicFinancePage = lazy(() =>
  import('../features/finance/ClinicFinancePage').then((m) => ({ default: m.ClinicFinancePage })),
)
const TasksPage = lazy(() =>
  import('../features/tasks/TasksPage').then((m) => ({ default: m.TasksPage })),
)

function RequireAuth({ children }: { children: ReactNode }) {
  const { token, loading } = useAuth()
  const location = useLocation()

  if (loading) return <div className="empty">Carregando...</div>
  if (!token) {
    const next = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }
  return <>{children}</>
}

interface NavItem {
  to: string
  icon: LucideIcon
  label: string
  end?: boolean
}

interface NavChild {
  to: string
  label: string
  // Atalho para a seção do domínio nas Configurações: aparece no fim do submenu e não acende o domínio.
  shortcut?: boolean
}
interface NavGroup {
  title: string | null
  items: NavChild[]
}
interface NavDomain {
  id: string
  icon: LucideIcon
  label: string
  items?: NavChild[]
  groups?: NavGroup[]
}

// Entradas das telas do núcleo financeiro: ficam atrás da mesma flag das telas (features/finance/featureFlag) e só
// aparecem no menu com ela ligada.
const FINANCE_CORE_NAV = new Set(['/financeiro-nucleo', '/financeiro-nucleo/eventos'])

// Os caminhos de todos os subitens: um subitem mais específico que também casa com a URL fica com o destaque
// (os avisos moram dentro de /financeiro-nucleo, e só eles acendem na página de avisos).
const NAV_SUBPATHS = (): string[] =>
  NAV_DOMAINS.flatMap(domainChildren).map((child) => child.to.split('?')[0])

// Itens diretos no trilho (clica, navega — sem flyout).
const NAV_LEAVES: NavItem[] = [
  { to: '/agenda', icon: Calendar, label: 'Agenda' },
  { to: '/pacientes', icon: UserRound, label: 'Pacientes' },
  { to: '/casos-cirurgicos', icon: ClipboardList, label: 'Casos cirúrgicos' },
  { to: '/cirurgias', icon: FileText, label: 'Cirurgias' },
]

// Domínios com filhos — abrem flyout (colapsado) ou acordeão (expandido).
const NAV_DOMAINS: NavDomain[] = [
  {
    id: 'comercial',
    icon: Briefcase,
    label: 'Comercial',
    groups: [
      {
        title: null,
        items: [
          { to: '/deals', label: 'Deals' },
          { to: '/tarefas', label: 'Tarefas' },
          { to: '/comissoes-comercial', label: 'Comissões comerciais' },
        ],
      },
      {
        title: null,
        items: [{ to: '/config/estagios', label: 'Configurar', shortcut: true }],
      },
    ],
  },
  {
    id: 'financeiro',
    icon: WalletCards,
    label: 'Financeiro',
    groups: [
      {
        title: null,
        items: [
          { to: '/financeiro-nucleo', label: 'Financeiro da clínica' },
          { to: '/financeiro-nucleo/eventos', label: 'Avisos do Asaas' },
          { to: '/financeiro-v2?tab=transactions', label: 'Transações' },
          { to: '/combinados', label: 'Combinados' },
          { to: '/financeiro-v2?tab=payouts', label: 'Repasses médicos' },
          { to: '/financeiro-v2?tab=payables', label: 'A pagar' },
          { to: '/financeiro-v2?tab=contracts', label: 'Contratos financeiros' },
          { to: '/assinaturas', label: 'Assinaturas' },
        ],
      },
      {
        title: null,
        items: [{ to: '/config/regras-repasse', label: 'Configurar', shortcut: true }],
      },
    ],
  },
]

function domainChildren(d: NavDomain): NavChild[] {
  return (d.items ?? (d.groups ?? []).flatMap((g) => g.items)).filter((c) => !c.shortcut)
}
function isDomainActive(d: NavDomain, pathname: string, search: string): boolean {
  return domainChildren(d).some((c) => {
    const [base, query] = c.to.split('?')
    // Filhos com query (ex.: /config?tab=stages) só valem na página exata e com
    // a query presente — senão qualquer /config/* acende vários domínios.
    if (query) return pathname === base && search.includes(query)
    return pathname === base || pathname.startsWith(base + '/')
  })
}

function initialsOf(name: string | null | undefined): string {
  if (!name) return '?'
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

function isStandalonePwa(): boolean {
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true
  )
}

function Layout() {
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED) === 'true',
  )
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [openDomain, setOpenDomain] = useState<string | null>(null)
  const [standalonePwa, setStandalonePwa] = useState(isStandalonePwa)
  const location = useLocation()
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const attr = document.documentElement.dataset.theme
    return attr === 'dark' ? 'dark' : 'light'
  })
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { user, logout } = useAuth()
  const commissionAccess = useCommissionAccess({
    query: { enabled: !!user, retry: false, refetchInterval: 60000 },
  })
  const financeCoreOn = isFinanceCoreUiEnabled(location.search)
  const navDomains = NAV_DOMAINS.map((domain) => ({
    ...domain,
    groups: domain.groups?.map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (item.to !== '/comissoes-comercial' || commissionAccess.data?.can_manage) &&
          (!FINANCE_CORE_NAV.has(item.to) || financeCoreOn),
      ),
    })),
  }))
  // Nas Configurações o menu principal começa só no trilho de ícones e a barra de seções ocupa o lugar dele, como na
  // Meta; o botão de expandir continua valendo. Não mexe na preferência salva: ao sair, o menu volta como estava.
  const inSettings = isSettingsPath(location.pathname)
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false)
  const railOnly = inSettings ? !settingsMenuOpen : collapsed
  const showSidebarLabels = !railOnly || mobileNavOpen

  const toggle = () => {
    if (inSettings) {
      setSettingsMenuOpen((open) => !open)
      return
    }
    setCollapsed((prev) => {
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, String(!prev))
      return !prev
    })
  }

  const closeMobileNav = () => setMobileNavOpen(false)

  // Fecha o flyout/acordeão ao navegar.
  useEffect(() => {
    setOpenDomain(null)
  }, [location.pathname, location.search])

  // Subitens que diferem só pelo ?tab (financeiro, configurações) compartilham
  // o mesmo pathname — o isActive do NavLink acenderia todos. Comparamos o
  // query do alvo com a URL atual pra acender só o tab corrente.
  const isSubitemActive = (to: string): boolean => {
    const [path, query] = to.split('?')
    const matches = (candidate: string) => location.pathname === candidate || location.pathname.startsWith(candidate + '/')
    if (!matches(path)) return false
    if (NAV_SUBPATHS().some((other) => other.startsWith(path + '/') && matches(other))) return false
    if (!query) return true
    const target = new URLSearchParams(query)
    const current = new URLSearchParams(location.search)
    for (const [key, value] of target) {
      if (current.get(key) !== value) return false
    }
    return true
  }

  const renderDomainChildren = (d: NavDomain) => {
    const link = (c: NavChild) => (
      <NavLink
        key={c.to}
        to={c.to}
        // Função (não string): desliga o active automático do NavLink, que casa
        // só por pathname e acenderia os 3 links /financeiro-v2?tab=* juntos.
        className={() =>
          c.shortcut
            ? 'sidebar-subitem sidebar-subitem-shortcut'
            : `sidebar-subitem ${isSubitemActive(c.to) ? 'active' : ''}`
        }
        onClick={() => {
          closeMobileNav()
          setOpenDomain(null)
        }}
      >
        {c.shortcut && <Settings size={12} aria-hidden />}
        {c.label}
      </NavLink>
    )
    if (d.items) return d.items.map(link)
    return (d.groups ?? []).map((g, gi) => (
      <div key={gi} className="sidebar-subgroup">
        {g.title && <div className="sidebar-subgroup-title">{g.title}</div>}
        {g.items.map(link)}
      </div>
    ))
  }

  const toggleTheme = () => {
    if (standalonePwa) return
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }

  useEffect(() => {
    const query = window.matchMedia('(display-mode: standalone)')
    const updateStandalone = () => setStandalonePwa(isStandalonePwa())
    updateStandalone()
    query.addEventListener('change', updateStandalone)
    return () => query.removeEventListener('change', updateStandalone)
  }, [])

  // O menu mobile (overlay) só faz sentido abaixo do breakpoint de 768px. Sem
  // isto, abrir o menu no mobile e depois alargar a janela deixa mobileNavOpen
  // preso em true → a sidebar fica colapsada (60px) mas renderiza os labels, que
  // vazam pra fora do rail. Fechar ao cruzar pro desktop evita esse estado.
  useEffect(() => {
    const query = window.matchMedia('(min-width: 769px)')
    const closeOnDesktop = () => {
      if (query.matches) setMobileNavOpen(false)
    }
    closeOnDesktop()
    query.addEventListener('change', closeOnDesktop)
    return () => query.removeEventListener('change', closeOnDesktop)
  }, [])

  useEffect(() => {
    const resolvedTheme = standalonePwa ? 'light' : theme
    document.documentElement.dataset.theme = resolvedTheme
    if (!standalonePwa) {
      localStorage.setItem(STORAGE_KEYS.THEME, theme)
    }
  }, [standalonePwa, theme])

  useEffect(() => {
    if (!userMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [userMenuOpen])

  useEffect(() => {
    if (!mobileNavOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mobileNavOpen])

  const firstName = (user?.name || '').split(' ')[0] || 'Você'

  return (
    <div className={`app ${mobileNavOpen ? 'mobile-nav-open' : ''}`}>
      <button
        type="button"
        className="mobile-menu-trigger"
        onClick={() => setMobileNavOpen(true)}
        aria-label="Abrir menu"
        aria-expanded={mobileNavOpen}
      >
        <Menu size={18} />
        <span>Menu</span>
      </button>
      <button
        type="button"
        className="mobile-sidebar-backdrop"
        onClick={closeMobileNav}
        aria-label="Fechar menu"
        tabIndex={mobileNavOpen ? 0 : -1}
      />
      <aside
        className={`sidebar ${railOnly ? 'sidebar-collapsed' : ''} ${
          mobileNavOpen ? 'sidebar-mobile-open' : ''
        }`}
        aria-label="Menu principal"
      >
        <div className="sidebar-brand">
          <span className="sidebar-brand-dot">I</span>
          {showSidebarLabels && <span className="sidebar-brand-label">IDF · Repasses</span>}
          <button
            type="button"
            className="mobile-sidebar-close"
            onClick={closeMobileNav}
            aria-label="Fechar menu"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {NAV_LEAVES.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              title={!showSidebarLabels ? item.label : undefined}
              onClick={() => {
                closeMobileNav()
                setOpenDomain(null)
              }}
            >
              <item.icon size={14} />
              {showSidebarLabels && <span>{item.label}</span>}
            </NavLink>
          ))}

          <div className="sidebar-divider" />

          {navDomains.map((d) => {
            const active = isDomainActive(d, location.pathname, location.search)
            const isOpen = openDomain === d.id
            return (
              <div key={d.id} className="sidebar-domain">
                <button
                  type="button"
                  className={`sidebar-item sidebar-domain-btn ${active ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                  title={!showSidebarLabels ? d.label : undefined}
                  aria-expanded={isOpen}
                  onClick={() => setOpenDomain(isOpen ? null : d.id)}
                >
                  <d.icon size={14} />
                  {showSidebarLabels && <span>{d.label}</span>}
                  {showSidebarLabels && (
                    <ChevronDown
                      size={12}
                      className={`sidebar-domain-chevron ${isOpen ? 'open' : ''}`}
                    />
                  )}
                </button>
                {showSidebarLabels && isOpen && (
                  <div className="sidebar-submenu-inline">{renderDomainChildren(d)}</div>
                )}
              </div>
            )
          })}

          <NavLink
            to={SETTINGS_HOME}
            // O destaque vale para tudo o que mora nas Configurações (/equipe, /profissionais, /config/…), não só a home.
            className={() => `sidebar-item sidebar-settings-link ${inSettings ? 'active' : ''}`}
            aria-current={inSettings ? 'page' : undefined}
            title={!showSidebarLabels ? 'Configurações' : undefined}
            onClick={() => {
              closeMobileNav()
              setOpenDomain(null)
            }}
          >
            <Settings size={14} />
            {showSidebarLabels && <span>Configurações</span>}
          </NavLink>
        </nav>

        <div className="sidebar-footer" ref={userMenuRef}>
          {user && (
            <>
              <button
                className="sidebar-user-btn"
                onClick={() => setUserMenuOpen((v) => !v)}
                title={!showSidebarLabels ? user.name : 'Menu do usuário'}
              >
                <span className="sidebar-avatar">{initialsOf(user.name)}</span>
                {showSidebarLabels && (
                  <div className="sidebar-user-info">
                    <span className="sidebar-user-name">{firstName}</span>
                    <span className="sidebar-user-role">{user.role}</span>
                  </div>
                )}
                {showSidebarLabels && (
                  <ChevronDown
                    size={12}
                    className={`sidebar-user-chevron ${userMenuOpen ? 'open' : ''}`}
                  />
                )}
              </button>
              {showSidebarLabels && !standalonePwa && (
                <button
                  type="button"
                  className="sidebar-theme-btn"
                  onClick={toggleTheme}
                  title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
                  aria-label={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
                >
                  {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                </button>
              )}
              {userMenuOpen && (
                <div className="sidebar-user-menu">
                  <div className="sidebar-user-menu-info">
                    <span className="sidebar-user-menu-name">{user.name}</span>
                    <span className="sidebar-user-menu-email">{user.email}</span>
                  </div>
                  <div className="sidebar-user-menu-divider" />
                  {(commissionAccess.data?.can_read_own ||
                    (commissionAccess.data?.can_review && !commissionAccess.data?.can_manage)) && (
                    <NavLink
                      className="sidebar-user-menu-item"
                      to={
                        commissionAccess.data.can_read_own
                          ? '/minhas-comissoes'
                          : '/minhas-comissoes/pendencias'
                      }
                      onClick={() => {
                        setUserMenuOpen(false)
                        closeMobileNav()
                      }}
                    >
                      <WalletCards size={15} />
                      <span>
                        {commissionAccess.data.can_read_own
                          ? 'Minhas comissões'
                          : 'Pendências de atribuição'}
                      </span>
                    </NavLink>
                  )}
                  <NavLink
                    className="sidebar-user-menu-item"
                    to="/minha-conta/senha"
                    onClick={() => {
                      setUserMenuOpen(false)
                      closeMobileNav()
                    }}
                  >
                    <KeyRound size={15} />
                    <span>Trocar senha</span>
                  </NavLink>
                  {!showSidebarLabels && !standalonePwa && (
                    <button
                      className="sidebar-user-menu-item"
                      onClick={() => {
                        toggleTheme()
                      }}
                    >
                      {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                      <span>{theme === 'dark' ? 'Modo claro' : 'Modo escuro'}</span>
                    </button>
                  )}
                  <button
                    className="sidebar-user-menu-item"
                    onClick={() => {
                      setUserMenuOpen(false)
                      logout()
                    }}
                  >
                    <LogOut size={15} />
                    <span>Sair</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
      {!showSidebarLabels &&
        openDomain &&
        (() => {
          const d = navDomains.find((x) => x.id === openDomain)
          if (!d) return null
          return (
            <div className="sidebar-flyout" onMouseLeave={() => setOpenDomain(null)}>
              <div className="sidebar-flyout-title">
                <d.icon size={16} />
                <span>{d.label}</span>
              </div>
              {renderDomainChildren(d)}
            </div>
          )
        })()}
      <div className="sidebar-toggle" onClick={toggle} title={railOnly ? 'Expandir' : 'Recolher'}>
        <div className="sidebar-toggle-icon">
          {railOnly ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </div>
      </div>
      <main className="main-area">
        <Topbar settings={inSettings} />
        <div className={`main-content ${inSettings ? 'main-content--settings' : ''}`}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}

function Topbar({ settings = false }: { settings?: boolean }) {
  const crumbs = useCurrentBreadcrumbs()
  // Nas Configurações a barra leva o título da área (como na Meta); só o registro aberto acrescenta o caminho.
  const pageCrumbs = useBreadcrumbOverride()
  return (
    <div className="topbar">
      {settings ? (
        <>
          <span className="topbar-title">Configurações</span>
          {pageCrumbs && pageCrumbs.length > 0 && (
            <>
              <span className="topbar-title-sep" aria-hidden />
              <Breadcrumbs crumbs={pageCrumbs} />
            </>
          )}
        </>
      ) : (
        <Breadcrumbs crumbs={crumbs} />
      )}
      <div className="topbar-grow" />
      <button type="button" className="topbar-search" title="Busca global — em breve">
        <Search size={13} />
        <span>Buscar deal, paciente, médico…</span>
        <span className="kbd">⌘K</span>
      </button>
    </div>
  )
}

function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  if (crumbs.length === 0) return null
  return (
    <nav className="topbar-crumb" aria-label="Breadcrumb">
      {crumbs.map((c, i) => (
        <span key={i} className="topbar-crumb-segment">
          {i > 0 && <span className="topbar-crumb-sep">/</span>}
          {c.to ? (
            <NavLink to={c.to} end>
              {c.label}
            </NavLink>
          ) : (
            <b>{c.label}</b>
          )}
        </span>
      ))}
    </nav>
  )
}

function FocusLayout() {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { user, logout } = useAuth()

  useEffect(() => {
    if (!menuOpen) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  return (
    <div className="focus-layout">
      <div className="focus-chip" ref={menuRef}>
        <button
          className="focus-chip-btn"
          onClick={() => setMenuOpen((prev) => !prev)}
          title={user?.name || 'Menu'}
        >
          <span className="focus-chip-logo">IDF</span>
        </button>
        {menuOpen && (
          <div className="focus-chip-menu">
            <div className="focus-chip-menu-info">
              <span className="focus-chip-menu-name">{user?.name}</span>
              <span className="focus-chip-menu-email">{user?.email}</span>
            </div>
            <div className="focus-chip-menu-divider" />
            <NavLink
              to={SETTINGS_HOME}
              className="focus-chip-menu-item"
              onClick={() => setMenuOpen(false)}
            >
              <Settings size={15} />
              <span>Configurações</span>
            </NavLink>
            <NavLink
              to="/minha-conta/senha"
              className="focus-chip-menu-item"
              onClick={() => setMenuOpen(false)}
            >
              <KeyRound size={15} />
              <span>Trocar senha</span>
            </NavLink>
            <button
              className="focus-chip-menu-item"
              onClick={() => {
                setMenuOpen(false)
                logout()
              }}
            >
              <LogOut size={15} />
              <span>Sair</span>
            </button>
          </div>
        )}
      </div>
      <Outlet />
    </div>
  )
}

// ── Route wrappers ─────────────────────────────────────────────────────────

type CanonicalProfessionalTab = 'visao-geral' | 'cirurgias' | 'repasses' | 'configuracoes'

const LEGACY_DOCTOR_TAB_ALIASES: Record<string, CanonicalProfessionalTab> = {
  '': 'visao-geral',
  'visao-geral': 'visao-geral',
  cirurgia: 'cirurgias',
  cirurgias: 'cirurgias',
  repasses: 'repasses',
  'repasses-v2': 'repasses',
  extrato: 'repasses',
  config: 'configuracoes',
  cadastro: 'configuracoes',
  configuracoes: 'configuracoes',
}

const ORPHAN_DOCTOR_TAB_ALIASES: Record<string, string> = {
  '': 'cirurgias',
  'visao-geral': 'cirurgias',
  cirurgia: 'cirurgias',
  cirurgias: 'cirurgias',
  repasses: 'repasses-v2',
  'repasses-v2': 'repasses-v2',
  extrato: 'repasses-v2',
  config: 'config',
  cadastro: 'config',
  configuracoes: 'config',
}

function legacyDoctorTabFromPath(pathname: string, doctorId: string) {
  const basePath = `/medicos/${doctorId}`
  if (!pathname.startsWith(basePath)) return ''
  const suffix = pathname.slice(basePath.length).replace(/^\/+/, '')
  return suffix.split('/')[0].toLowerCase()
}

function canonicalTabForLegacyDoctorPath(pathname: string, doctorId: string) {
  const legacyTab = legacyDoctorTabFromPath(pathname, doctorId)
  return LEGACY_DOCTOR_TAB_ALIASES[legacyTab] ?? 'visao-geral'
}

function DoctorPageRoute({
  forcedTab,
  orphanLegacyTab,
}: {
  forcedTab?: CanonicalProfessionalTab
  orphanLegacyTab?: string
} = {}) {
  const { doctorId } = useParams<{ doctorId: string }>()
  const location = useLocation()
  const parsedDoctorId = Number(doctorId)
  const validDoctorId = Number.isInteger(parsedDoctorId) && parsedDoctorId > 0
  const professionalsQ = useListProfessionalsEndpointApiProfessionalsGet(
    { doctor_id: validDoctorId ? parsedDoctorId : undefined },
    { query: { enabled: validDoctorId } },
  )
  const professional = professionalsQ.data?.[0]

  if (!doctorId || !validDoctorId) {
    return <Navigate to="/profissionais" replace />
  }

  if (professionalsQ.isLoading) {
    return <div className="empty">Localizando cadastro profissional...</div>
  }

  if (professional) {
    const tab = forcedTab ?? canonicalTabForLegacyDoctorPath(location.pathname, doctorId)
    return (
      <Navigate
        to={`/profissionais/${professional.id}/${tab}${location.search}${location.hash}`}
        replace
      />
    )
  }

  if (professionalsQ.isError) {
    return (
      <div className="empty" role="alert">
        <p>Não foi possível localizar o cadastro profissional deste médico.</p>
        <button
          type="button"
          className="btn"
          onClick={() => void professionalsQ.refetch()}
          disabled={professionalsQ.isFetching}
        >
          {professionalsQ.isFetching ? 'Tentando novamente...' : 'Tentar novamente'}
        </button>
      </div>
    )
  }

  // Doctor legado sem Professional: mantém a tela antiga temporariamente.
  // Normaliza só até uma sub-rota compreendida pelo DoctorPage. Ao chegar
  // nela a condição deixa de ser verdadeira, portanto não há loop.
  const currentLegacyTab = legacyDoctorTabFromPath(location.pathname, doctorId)
  const fallbackLegacyTab =
    orphanLegacyTab ?? ORPHAN_DOCTOR_TAB_ALIASES[currentLegacyTab] ?? 'cirurgias'
  if (currentLegacyTab !== fallbackLegacyTab) {
    return (
      <Navigate
        to={`/medicos/${doctorId}/${fallbackLegacyTab}${location.search}${location.hash}`}
        replace
      />
    )
  }

  return <DoctorPage doctorId={parsedDoctorId} />
}

function SurgeriesPageRoute() {
  const { doctors } = useDoctors()
  return <SurgeriesPage doctors={doctors} />
}

function ReceivablesPageRoute() {
  const { doctors } = useDoctors()
  return <ReceivablesPage doctors={doctors} />
}

function SurgeryDetailRoute() {
  const { id } = useParams<{ id: string }>()
  const { doctors } = useDoctors()
  return (
    <SurgeryDetailPage
      surgeryId={parseInt(id || '0', 10)}
      doctors={doctors}
      renderNotionPublish={(props) => (
        <Suspense fallback={null}>
          <NotionPublishModal {...props} />
        </Suspense>
      )}
    />
  )
}

/**
 * O financeiro novo dentro da cirurgia (página, gaveta financeira e aba do caso), fornecido à seção financeira pelo
 * `CoreFinanceSlot`. O próprio Suspense segura só a seção enquanto o pedaço do núcleo carrega, não a tela inteira.
 */
function EmbeddedSurgeryFinance({ surgeryId }: { surgeryId: number }) {
  return (
    <Suspense fallback={<div className="empty">Carregando o financeiro da cirurgia…</div>}>
      <SurgeryFinanceEmbedded surgeryId={surgeryId} />
    </Suspense>
  )
}

const renderEmbeddedSurgeryFinance = (surgeryId: number) => <EmbeddedSurgeryFinance surgeryId={surgeryId} />

function SurgeryFinanceRoute() {
  const { surgeryId } = useParams<{ surgeryId: string }>()
  return <SurgeryFinancePage surgeryId={parseInt(surgeryId || '0', 10)} />
}

function CommercialProposalRoute() {
  const { id } = useParams<{ id: string }>()
  return <ProposalPage commercialSection={<CommissionCard proposalId={Number(id)} />} />
}

function ProposalEditorRoute() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  return (
    <ProposalEditorPage
      proposalId={parseInt(id || '0', 10)}
      currentUserId={user?.id ?? null}
      currentUserRole={user?.role ?? null}
    />
  )
}

/** Redirect /propostas/* → /deals/* (rotas antigas continuam navegáveis
 *  via Link/href, mas a URL canônica passa a ser /deals). */
function PropostasRedirect() {
  const location = useLocation()
  const tail = location.pathname.replace(/^\/propostas/, '') // ex: '/42/editor' ou ''
  return <Navigate to={`/deals${tail}${location.search}`} replace />
}

function RepassesRedirect() {
  return <DoctorPageRoute forcedTab="repasses" orphanLegacyTab="repasses-v2" />
}

function HomeRedirect() {
  return <Navigate to="/agenda" replace />
}

function SetPasswordRoute() {
  const { adoptSession } = useAuth()
  return <SetPasswordPage onSession={adoptSession} />
}

function ChangePasswordRoute() {
  const { adoptSession } = useAuth()
  return <ChangePasswordPage onSession={adoptSession} />
}

// A Equipe recebe do shell quem está vendo e os médicos (features não importam app/).
function useTeamContext() {
  const { user } = useAuth()
  const { doctors } = useDoctors()
  return {
    viewer: { id: user?.id ?? null, isAdmin: user?.role === 'admin' },
    doctors,
  }
}

// /equipe e /equipe/:id são a mesma tela: a lista inteira ou a lista com a ficha da pessoa ao lado.
function TeamRoute() {
  return (
    <TeamPage
      {...useTeamContext()}
      renderCommercialSetup={(userId) => <AgentConfiguration userId={userId} />}
    />
  )
}

function AuthenticatedShell() {
  return <Outlet />
}

function AppContent() {
  return (
    <Suspense fallback={<div className="empty">Carregando…</div>}>
      <Routes>
        <Route path="login" element={<LoginPage />} />
        <Route path="esqueci-senha" element={<ForgotPasswordPage />} />
        <Route path="definir-senha/:token" element={<SetPasswordRoute />} />
        <Route path="repasses-medico/:token" element={<PublicDoctorRepassesPage />} />
        <Route
          element={
            <RequireAuth>
              <DoctorsProvider>
                <AuthenticatedShell />
              </DoctorsProvider>
            </RequireAuth>
          }
        >
          {/* Print view — no chrome. Loads the document read-only and fires
            window.print() so the user's own browser renders the PDF.
            Aceita /deals/:id/print (canônica) e /propostas/:id/print (alias). */}
          <Route path="deals/:id/print" element={<ProposalPrintPage />} />
          <Route path="propostas/:id/print" element={<ProposalPrintPage />} />

          {/* Modo palco — readonly, sem chrome. Tela que o vendedor mostra ao
            paciente (2ª janela), atualiza ao vivo pelo BroadcastChannel. */}
          <Route path="deals/:id/apresentar" element={<ProposalPresentPage />} />
          <Route path="propostas/:id/apresentar" element={<ProposalPresentPage />} />

          {/* Focus layout (minimal chrome) — fullscreen editor. */}
          <Route element={<FocusLayout />}>
            <Route path="deals/:id/editor" element={<ProposalEditorRoute />} />
            <Route path="propostas/:id/editor" element={<ProposalEditorRoute />} />
          </Route>

          {/* Main layout (sidebar + top bar) */}
          <Route
            element={
              <BreadcrumbProvider>
                <Layout />
              </BreadcrumbProvider>
            }
          >
            <Route index element={<HomeRedirect />} />

            {/* Comercial — Deals + Tarefas */}
            <Route path="deals" element={<ProposalsPage />} />
            <Route path="deals/arquivadas" element={<ProposalsPage mode="archived" />} />
            <Route path="deals/nova" element={<Navigate to="/deals" replace />} />
            <Route path="deals/:id" element={<CommercialProposalRoute />} />
            <Route path="comissoes-comercial" element={<CommissionsPage />} />
            <Route path="comissoes-comercial/pendencias" element={<CommissionsPage />} />
            <Route path="minhas-comissoes" element={<CommissionsPage />} />
            <Route path="minhas-comissoes/pendencias" element={<CommissionsPage />} />
            <Route path="minha-conta/senha" element={<ChangePasswordRoute />} />
            <Route path="tarefas" element={<TasksPage />} />
            <Route path="dashboard" element={<Navigate to="/agenda" replace />} />

            {/* Aliases legados — /propostas/* e /hoje continuam navegáveis
              redirecionando pra rota canônica. Links antigos não quebram. */}
            <Route path="propostas/*" element={<PropostasRedirect />} />
            <Route path="hoje" element={<Navigate to="/tarefas" replace />} />

            <Route path="repasses/:doctorId" element={<RepassesRedirect />} />
            <Route path="agenda" element={<AgendaPage />} />
            <Route path="pacientes" element={<PatientsPage />} />
            <Route path="pacientes/feegow/:id" element={<PatientFeegowBridgePage />} />
            <Route path="pacientes/:id" element={<PatientDossierPage />} />
            <Route path="casos-cirurgicos" element={<SurgicalCasesPage />} />
            <Route path="casos-cirurgicos/:caseId" element={<SurgicalCaseDetailPage />} />
            <Route path="cirurgias" element={<SurgeriesPageRoute />} />
            <Route path="cirurgias/:id" element={<SurgeryDetailRoute />} />
            <Route
              path="cirurgias/profissional/:professionalId"
              element={<ProfessionalWorkPage view="cirurgias" />}
            />
            <Route path="combinados" element={<ReceivablesPageRoute />} />
            <Route
              path="pagamentos"
              element={<Navigate to="/financeiro-v2?tab=payouts" replace />}
            />
            <Route path="financeiro" element={<Navigate to="/financeiro-v2" replace />} />
            <Route path="financeiro-v2" element={<FinancialV2Page />} />
            <Route
              path="financeiro-v2/repasses/:professionalId"
              element={<ProfessionalWorkPage view="repasses" />}
            />
            <Route path="financeiro-nucleo" element={<ClinicFinancePage />} />
            <Route path="financeiro-nucleo/cirurgias" element={<ClinicFinancePage />} />
            <Route
              path="financeiro-nucleo/cirurgias/:surgeryId"
              element={<SurgeryFinanceRoute />}
            />
            <Route path="financeiro-nucleo/eventos" element={<ProviderEventsPage />} />
            <Route path="assinaturas" element={<SignaturesPage />} />
            {/* Configurações (engrenagem): barra de seções + item escolhido, no modelo da Meta. Os endereços de
              cada tela continuam os mesmos; os antigos redirecionam. */}
            <Route element={<SettingsLayout />}>
              <Route path="configuracoes" element={<SettingsHome />} />

              {/* Pessoas e empresas */}
              <Route path="equipe" element={<TeamRoute />} />
              <Route path="equipe/:userId" element={<TeamRoute />} />
              <Route path="profissionais" element={<ProfessionalsPage />} />
              <Route path="profissionais/:professionalId/*" element={<ProfessionalsPage />} />
              <Route path="medicos" element={<Navigate to="/profissionais" replace />} />
              <Route path="medicos/:doctorId/*" element={<DoctorPageRoute />} />
              <Route path="fornecedores" element={<SuppliersPage />} />
              <Route path="fornecedores/:supplierId/*" element={<SuppliersPage />} />
              <Route
                path="config/portal-parceiro"
                element={<ConfigPage section="portal-parceiro" />}
              />

              {/* Catálogo */}
              <Route path="config/procedimentos" element={<ProcedureCatalogPage />} />
              <Route path="config/procedimentos/:procedureId" element={<ProcedureCatalogPage />} />
              <Route path="config/pacotes" element={<ProcedureCatalogPage section="pacotes" />} />
              <Route
                path="config/pacotes/:packageId"
                element={<ProcedureCatalogPage section="pacotes" />}
              />
              <Route path="config/servicos-proprios" element={<ClinicServicesPage />} />
              <Route path="config/requisitos" element={<RequirementsPage />} />
              <Route path="config/requisitos/:requirementId" element={<RequirementsPage />} />
              <Route path="config/precos-procedimentos" element={<SupplierPricesPage />} />
              <Route path="config/tabelas-custo" element={<SupplierCostTablesPage />} />
              <Route path="config/tabelas-custo/:tableId" element={<SupplierCostTablesPage />} />
              <Route path="config/tabelas-precos" element={<PriceTablesPage />} />
              <Route path="config/tabelas-precos/:tableId" element={<PriceTablesPage />} />

              {/* Comercial */}
              <Route path="config/estagios" element={<ConfigPage section="estagios" />} />
              <Route path="config/cadencias" element={<ConfigPage section="cadencias" />} />
              <Route path="config/motivos" element={<ConfigPage section="motivos" />} />

              {/* Financeiro */}
              <Route path="config/regras-repasse" element={<RepasseRulesPage />} />
              <Route path="config/regras-repasse/:counterpartyId" element={<RepasseRulesPage />} />
              <Route path="config/metodos-pagamento" element={<PaymentMethodsPage />} />
              <Route path="config/metodos-pagamento/:methodId" element={<PaymentMethodsPage />} />
              <Route path="config/contas-bancarias" element={<BankAccountsPage />} />
              <Route path="config/contas-bancarias/:accountId" element={<BankAccountsPage />} />

              {/* Integrações */}
              <Route
                path="config/integracoes"
                element={<ConfigPage section="integracoes" before={<NotionImportBanner />} />}
              />
              <Route path="config/tags-notion" element={<ConfigPage section="tags-notion" />} />

              {/* Sistema */}
              <Route path="logs" element={<LogsPage />} />

              {/* Endereços antigos */}
              <Route path="config" element={<ConfigLegacyRedirect />} />
              <Route
                path="config/pagamentos"
                element={<Navigate to="/config/metodos-pagamento" replace />}
              />
              <Route
                path="config/contas"
                element={<Navigate to="/config/contas-bancarias" replace />}
              />
            </Route>
            <Route path="*" element={<HomeRedirect />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}

function AppErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 32,
        textAlign: 'center',
        gap: 14,
      }}
      role="alert"
    >
      <div style={{ fontSize: 22, fontWeight: 700 }}>Algo deu errado carregando esta página.</div>
      <div style={{ color: 'var(--text-secondary)', maxWidth: 480 }}>
        O erro foi reportado para o time. Você pode tentar recarregar ou voltar ao início.
      </div>
      <pre
        style={{
          fontSize: 12,
          color: 'var(--text-muted)',
          maxWidth: 520,
          whiteSpace: 'pre-wrap',
        }}
      >
        {error instanceof Error ? error.message : String(error)}
      </pre>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn" onClick={() => window.location.reload()}>
          Recarregar
        </button>
        <button className="btn btn-primary" onClick={resetError}>
          Tentar novamente
        </button>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <AppErrorFallback error={error} resetError={resetError} />
      )}
    >
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <CoreFinanceSlotContext.Provider value={renderEmbeddedSurgeryFinance}>
              <AppContent />
            </CoreFinanceSlotContext.Provider>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}
